import type {
  CatalogBillingPromptTierBasis,
  QuotationCatalogResponse,
  QuotationNamespace,
  QuotationSku,
  QuotationTokenPrices,
  QuotationTokenTierRow,
  QuotationUnitPrice,
} from '@nadoutong/contracts';
import type { QuotationCurrency } from '@nadoutong/contracts';
import { effectiveSkuZhe, namespaceDraft } from './quotationDraft.ts';
import type { QuotationDraft, QuotationNamespaceDraft } from './quotationDraft.ts';

export type Cell = string | number | null;

/** 1-based Excel coordinates. The header occupies row 1, so the first data row is 2. */
export interface SheetMerge {
  readonly column: number;
  readonly fromRow: number;
  readonly toRow: number;
}

export interface Sheet {
  readonly name: string;
  readonly header: readonly string[];
  readonly rows: readonly (readonly Cell[])[];
  readonly merges: readonly SheetMerge[];
  readonly widths: readonly number[];
}

export interface QuotationSheets {
  readonly summary: Sheet;
  readonly text: Sheet | null;
  readonly image: Sheet | null;
  readonly video: Sheet | null;
  readonly terms: Sheet;
}

export interface IncludedNamespace {
  readonly namespace: QuotationNamespace;
  readonly entry: QuotationNamespaceDraft;
}

const FILL_YELLOW = 'FFFFF2CC';
const NUMBER_FORMAT = '0.####';
const INTEGER_FORMAT = '0';
const BUSINESS_TZ_OFFSET_MS = 8 * 60 * 60 * 1000;
const DEFAULT_COLUMN = '默认';

const SUMMARY_NAME = '优惠价格汇总';
const TEXT_NAME = '文本类模型价格表';
const IMAGE_NAME = '图片类模型价格表';
const VIDEO_NAME = '视频类模型价格表';
const TERMS_NAME = '口径说明';

const BASIS_LABEL = {
  input_tokens: '输入token',
  input_plus_cache_tokens: '输入+缓存token',
  varies_by_ingress: '随入口变化',
} as const satisfies Record<CatalogBillingPromptTierBasis, string>;

const IMAGE_DIMS = [
  { key: 'input', label: '输入' },
  { key: 'output', label: '输出' },
  { key: 'cache_read', label: '缓存读' },
  { key: 'cache_write', label: '缓存写' },
  { key: 'cache_write_5m', label: '缓存写5m' },
  { key: 'cache_write_1h', label: '缓存写1h' },
  { key: 'reasoning', label: '推理' },
  { key: 'image_input', label: '图片输入' },
] as const;

interface SkuContext {
  readonly sku: QuotationSku;
  readonly vendor: string;
  readonly channel: string;
  readonly zhe: number | null;
  readonly discount: string;
}

interface SummaryLine {
  readonly category: string;
  readonly vendor: string;
  readonly channel: string;
  readonly model: string;
  readonly spec: string;
  readonly discount: string;
  readonly unit: string;
  readonly list: number | null;
  readonly net: number | null;
  readonly note: string;
}

interface TextOptional {
  readonly cacheWrite1h: boolean;
  readonly reasoning: boolean;
  readonly imageInput: boolean;
}

interface TextLine {
  readonly vendor: string;
  readonly channel: string;
  readonly model: string;
  readonly attr: string;
  readonly unit: string;
  readonly discount: string;
  readonly note: string;
  readonly zhe: number | null;
  readonly input: number | null;
  readonly cacheRead: number | null;
  readonly cacheWrite: number | null;
  readonly output: number | null;
  readonly cacheWrite1h: number | null;
  readonly reasoning: number | null;
  readonly imageInput: number | null;
}

interface PriceLine {
  readonly vendor: string;
  readonly channel: string;
  readonly model: string;
  readonly spec: string;
  readonly unit: string;
  readonly usd: number | null;
  readonly zhe: number | null;
  readonly discount: string;
  readonly note: string;
}

interface VideoCell {
  readonly raw: string | null;
  readonly usd: number;
}

interface VideoBucket {
  readonly surcharge: boolean;
  readonly attr: string;
  readonly unit: string;
  readonly cells: Map<string, VideoCell>;
  readonly inputs: number[];
}

interface BuiltCategory {
  readonly sheet: Sheet | null;
  readonly summary: readonly SummaryLine[];
}

/** Integers use '0' so Excel does not render a trailing decimal point. */
export function numberFormatFor(value: number): string {
  return Number.isInteger(value) ? INTEGER_FORMAT : NUMBER_FORMAT;
}

function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** `YYYY-MM-DD HH:mm Asia/Shanghai`. Unparseable input is returned unchanged. */
function formatShanghai(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const wall = new Date(ms + BUSINESS_TZ_OFFSET_MS);
  const date = [wall.getUTCFullYear(), pad2(wall.getUTCMonth() + 1), pad2(wall.getUTCDate())].join(
    '-',
  );
  return `${date} ${pad2(wall.getUTCHours())}:${pad2(wall.getUTCMinutes())} Asia/Shanghai`;
}

function fmtTokens(value: number): string {
  if (value % 1_000_000 === 0 && value >= 1_000_000) return `${value / 1_000_000}M`;
  if (value % 1000 === 0 && value >= 1000) return `${value / 1000}k`;
  return String(value);
}

function currencyWord(currency: QuotationCurrency): string {
  return currency === 'CNY' ? '元' : 'USD';
}

function tokenUnit(currency: QuotationCurrency): string {
  return `${currencyWord(currency)}/百万tokens`;
}

function perUnit(currency: QuotationCurrency, label: string): string {
  return `${currencyWord(currency)}/${label}`;
}

function sameRateUnit(currency: QuotationCurrency): string {
  return `${tokenUnit(currency)}(输入输出同价)`;
}

/** Sheet currency amount. `null` stays an empty cell, never 0. */
function listPrice(usd: number | null, fx: number): number | null {
  if (usd === null) return null;
  return round4(usd * fx);
}

/** 折后 from the unrounded USD amount, not from the already-rounded 原价. */
function netPrice(usd: number | null, fx: number, zhe: number | null): number | null {
  if (usd === null || zhe === null) return null;
  return round4((usd * fx * zhe) / 10);
}

function modelLabel(sku: QuotationSku, sub: boolean): string {
  const name = sku.model_display_name ?? sku.model;
  return sub ? `↳ ${name}` : name;
}

function basisLabel(basis: CatalogBillingPromptTierBasis | null): string {
  if (basis === null) return '';
  return BASIS_LABEL[basis];
}

function widthFor(header: string): number {
  if (header === '厂商') return 14;
  if (header === '渠道') return 18;
  if (header === '模型名称') return 34;
  if (header === '属性' || header === '规格' || header === '规格/属性') return 28;
  if (header === '折扣率') return 9;
  if (header === '计价单位') return 14;
  if (header === '备注') return 46;
  if (header === '类别') return 8;
  if (header === '项') return 16;
  if (header === '说明') return 90;
  return 12;
}

function skuContext(included: IncludedNamespace, sku: QuotationSku): SkuContext {
  const zhe = effectiveSkuZhe(included.entry, sku.sku_id);
  return {
    sku,
    vendor: sku.vendor_display_name,
    channel: included.namespace.namespace_display_name,
    zhe,
    discount: zhe === null ? '' : `${zhe}折`,
  };
}

function appendRun(merges: SheetMerge[], keys: readonly string[], column: number): void {
  let start = 0;
  for (let index = 1; index <= keys.length; index += 1) {
    const current = index < keys.length ? keys[index] : undefined;
    const origin = keys[start];
    if (current === undefined || origin === undefined || current !== origin) {
      if (index - start > 1) {
        merges.push({ column, fromRow: start + 2, toRow: index + 1 });
      }
      start = index;
    }
  }
}

interface MergeIdentity {
  readonly vendor: string;
  readonly channel: string;
  /** Set on the summary sheet so a run stops at a category boundary. */
  readonly category?: string;
}

function columnMerges(
  rows: readonly MergeIdentity[],
  vendorColumn: number,
  channelColumn: number,
): readonly SheetMerge[] {
  const merges: SheetMerge[] = [];
  appendRun(
    merges,
    rows.map((row) =>
      row.category === undefined ? row.vendor : `${row.category}\0${row.vendor}`,
    ),
    vendorColumn,
  );
  appendRun(
    merges,
    rows.map((row) => {
      const pair = `${row.vendor}\0${row.channel}`;
      return row.category === undefined ? pair : `${row.category}\0${pair}`;
    }),
    channelColumn,
  );
  return merges;
}

function finishSheet(
  name: string,
  header: readonly string[],
  rows: readonly (readonly Cell[])[],
  mergeRows: readonly MergeIdentity[],
  vendorColumn: number,
  channelColumn: number,
): Sheet {
  return {
    name,
    header,
    rows,
    merges: columnMerges(mergeRows, vendorColumn, channelColumn),
    widths: header.map(widthFor),
  };
}

export function includedNamespaces(
  catalog: QuotationCatalogResponse,
  draft: QuotationDraft,
): readonly IncludedNamespace[] {
  const included: IncludedNamespace[] = [];
  for (const namespace of catalog.namespaces) {
    const entry = namespaceDraft(draft, namespace.namespace_slug);
    if (!entry.included) continue;
    included.push({ namespace, entry });
  }
  return included;
}

function cacheWriteOf(prices: QuotationTokenPrices): number | null {
  return prices.cache_write ?? prices.cache_write_5m;
}

function textDims(
  rates: QuotationTokenPrices | null,
  single: number | null,
): Pick<
  TextLine,
  'input' | 'cacheRead' | 'cacheWrite' | 'output' | 'cacheWrite1h' | 'reasoning' | 'imageInput'
> {
  if (rates === null) {
    return {
      input: single,
      cacheRead: null,
      cacheWrite: null,
      output: single,
      cacheWrite1h: null,
      reasoning: null,
      imageInput: null,
    };
  }
  return {
    input: rates.input,
    cacheRead: rates.cache_read,
    cacheWrite: cacheWriteOf(rates),
    output: rates.output,
    cacheWrite1h: rates.cache_write_1h,
    reasoning: rates.reasoning,
    imageInput: rates.image_input,
  };
}

function textLine(
  ctx: SkuContext,
  currency: QuotationCurrency,
  attr: string,
  sub: boolean,
  rates: QuotationTokenPrices | null,
  single: number | null,
  note: string,
): TextLine {
  return {
    vendor: ctx.vendor,
    channel: ctx.channel,
    model: modelLabel(ctx.sku, sub),
    attr,
    unit: tokenUnit(currency),
    discount: ctx.discount,
    note,
    zhe: ctx.zhe,
    ...textDims(rates, single),
  };
}

/** Non-empty axis text, otherwise `每` + the unit (a surcharge variant_key is often ''). */
function surchargeLabel(price: QuotationUnitPrice): string {
  const variant = price.variant_label;
  if (variant !== null && variant.length > 0) return variant;
  return `每${price.unit_label.zh}`;
}

function surchargeNote(
  price: QuotationUnitPrice,
  currency: QuotationCurrency,
  fx: number,
  zhe: number | null,
): string {
  const label = surchargeLabel(price);
  const unit = perUnit(currency, price.unit_label.zh);
  const listed = listPrice(price.usd_per_unit, fx);
  const net = netPrice(price.usd_per_unit, fx, zhe);
  const amount = listed === null ? '' : String(listed);
  const base = `附加费 ${label}：原价 ${amount} ${unit}`;
  if (net === null) return base;
  return `${base}，折后 ${String(net)}`;
}

function textSurchargeNotes(
  sku: QuotationSku,
  currency: QuotationCurrency,
  fx: number,
  zhe: number | null,
): string {
  if (sku.unit_prices === null) return '';
  const notes: string[] = [];
  for (const price of sku.unit_prices) {
    if (price.kind !== 'surcharge') continue;
    notes.push(surchargeNote(price, currency, fx, zhe));
  }
  return notes.join('；');
}

function textOptionalOf(lines: readonly TextLine[]): TextOptional {
  return {
    cacheWrite1h: lines.some((line) => line.cacheWrite1h !== null),
    reasoning: lines.some((line) => line.reasoning !== null),
    imageInput: lines.some((line) => line.imageInput !== null),
  };
}

function textHeader(optional: TextOptional): string[] {
  const list = ['输入(原价)', '缓存读(原价)', '缓存写(原价)', '输出(原价)'];
  const net = ['输入(折后)', '缓存读(折后)', '缓存写(折后)', '输出(折后)'];
  if (optional.cacheWrite1h) {
    list.push('缓存写1h(原价)');
    net.push('缓存写1h(折后)');
  }
  if (optional.reasoning) {
    list.push('推理(原价)');
    net.push('推理(折后)');
  }
  if (optional.imageInput) {
    list.push('图片输入(原价)');
    net.push('图片输入(折后)');
  }
  return ['厂商', '渠道', '模型名称', '属性', '计价单位', ...list, ...net, '折扣率', '备注'];
}

function textValues(line: TextLine, optional: TextOptional): (number | null)[] {
  const values: (number | null)[] = [line.input, line.cacheRead, line.cacheWrite, line.output];
  if (optional.cacheWrite1h) values.push(line.cacheWrite1h);
  if (optional.reasoning) values.push(line.reasoning);
  if (optional.imageInput) values.push(line.imageInput);
  return values;
}

function textCells(line: TextLine, optional: TextOptional, fx: number): Cell[] {
  const values = textValues(line, optional);
  const cells: Cell[] = [line.vendor, line.channel, line.model, line.attr, line.unit];
  for (const usd of values) cells.push(listPrice(usd, fx));
  for (const usd of values) cells.push(netPrice(usd, fx, line.zhe));
  cells.push(line.discount, line.note);
  return cells;
}

function textSummaryNote(line: TextLine, fx: number): string {
  const parts: string[] = [];
  const input = listPrice(line.input, fx);
  const cacheRead = listPrice(line.cacheRead, fx);
  const cacheWrite = listPrice(line.cacheWrite, fx);
  if (input !== null) parts.push(`输入 ${String(input)}`);
  if (cacheRead !== null) parts.push(`缓存读 ${String(cacheRead)}`);
  if (cacheWrite !== null) parts.push(`缓存写 ${String(cacheWrite)}`);
  return parts.join('｜');
}

/**
 * Catalog `min_prompt_tokens` is the first token above the band (100001, 272001).
 * When that is one past a positive thousand, print the round edge with ≤ / >.
 */
function promptBandEdge(minTokens: number): { readonly round: boolean; readonly tokens: number } {
  const below = minTokens - 1;
  if (below > 0 && below % 1000 === 0) return { round: true, tokens: below };
  return { round: false, tokens: minTokens };
}

function baseLengthAttr(minTokens: number): string {
  const band = promptBandEdge(minTokens);
  const mark = band.round ? '≤' : '<';
  return `输入长度 ${mark}${fmtTokens(band.tokens)}`;
}

function tierAttr(minTokens: number, basis: CatalogBillingPromptTierBasis | null): string {
  const band = promptBandEdge(minTokens);
  const mark = band.round ? '>' : '≥';
  return `输入长度 ${mark}${fmtTokens(band.tokens)}（${basisLabel(basis)}）`;
}

function tokenTierSingle(row: QuotationTokenTierRow): number | null {
  if (row.rates !== null) return null;
  return row.single_rate_per_mtok;
}

function textSummaryOf(line: TextLine, fx: number): SummaryLine {
  return {
    category: '文本',
    vendor: line.vendor,
    channel: line.channel,
    model: line.model,
    spec: line.attr,
    discount: line.discount,
    unit: line.unit,
    list: listPrice(line.output, fx),
    net: netPrice(line.output, fx, line.zhe),
    note: textSummaryNote(line, fx),
  };
}

function textUnitSpec(price: QuotationUnitPrice): string {
  if (price.kind === 'surcharge') return `附加费：${surchargeLabel(price)}`;
  const label = price.variant_label;
  if (label !== null && label.length > 0) return label;
  return '';
}

/** Unit-mode text SKUs stay off the text sheet; each price is one summary row. */
function textUnitSummaries(
  ctx: SkuContext,
  currency: QuotationCurrency,
  fx: number,
  sku: QuotationSku,
): SummaryLine[] {
  if (sku.unit_prices === null) return [];
  return sku.unit_prices.map((price, index) => ({
    category: '文本',
    vendor: ctx.vendor,
    channel: ctx.channel,
    model: modelLabel(sku, index > 0),
    spec: textUnitSpec(price),
    discount: ctx.discount,
    unit: perUnit(currency, price.unit_label.zh),
    list: listPrice(price.usd_per_unit, fx),
    net: netPrice(price.usd_per_unit, fx, ctx.zhe),
    note: '',
  }));
}

function buildText(
  included: readonly IncludedNamespace[],
  currency: QuotationCurrency,
  fx: number,
): BuiltCategory {
  const lines: TextLine[] = [];
  const summary: SummaryLine[] = [];
  for (const entry of included) {
    for (const sku of entry.namespace.skus) {
      if (sku.category !== 'text') continue;
      const ctx = skuContext(entry, sku);
      if (sku.billing_mode === 'unit') {
        summary.push(...textUnitSummaries(ctx, currency, fx, sku));
        continue;
      }
      if (sku.billing_mode !== 'token') continue;
      const tiers = sku.prompt_length_tiers;
      const firstTier = tiers !== null && tiers.length > 0 ? tiers[0] : undefined;
      const baseAttr = firstTier !== undefined ? baseLengthAttr(firstTier.min_prompt_tokens) : '';
      const baseLine = textLine(
        ctx,
        currency,
        baseAttr,
        false,
        sku.token_prices,
        null,
        textSurchargeNotes(sku, currency, fx, ctx.zhe),
      );
      lines.push(baseLine);
      summary.push(textSummaryOf(baseLine, fx));
      if (tiers !== null) {
        for (const tier of tiers) {
          const tierLine = textLine(
            ctx,
            currency,
            tierAttr(tier.min_prompt_tokens, sku.prompt_length_basis),
            true,
            tier.rates,
            null,
            '',
          );
          lines.push(tierLine);
          summary.push(textSummaryOf(tierLine, fx));
        }
      }
      if (sku.token_tier_rows !== null) {
        for (const row of sku.token_tier_rows) {
          const variantLine = textLine(
            ctx,
            currency,
            row.variant_label,
            true,
            row.rates,
            tokenTierSingle(row),
            '',
          );
          lines.push(variantLine);
          summary.push(textSummaryOf(variantLine, fx));
        }
      }
    }
  }
  if (lines.length === 0) return { sheet: null, summary };
  const optional = textOptionalOf(lines);
  const header = textHeader(optional);
  return {
    sheet: finishSheet(
      TEXT_NAME,
      header,
      lines.map((line) => textCells(line, optional, fx)),
      lines,
      1,
      2,
    ),
    summary,
  };
}

function dimUsd(prices: QuotationTokenPrices, key: (typeof IMAGE_DIMS)[number]['key']): number | null {
  return prices[key];
}

function hasImageDim(prices: QuotationTokenPrices): boolean {
  return IMAGE_DIMS.some((dim) => dimUsd(prices, dim.key) !== null);
}

function pushPrice(
  lines: PriceLine[],
  ctx: SkuContext,
  currency: QuotationCurrency,
  spec: string,
  unit: string,
  usd: number | null,
  sub: boolean,
  note: string,
): void {
  lines.push({
    vendor: ctx.vendor,
    channel: ctx.channel,
    model: modelLabel(ctx.sku, sub),
    spec,
    unit,
    usd,
    zhe: ctx.zhe,
    discount: ctx.discount,
    note,
  });
}

function pushUnitPrice(
  lines: PriceLine[],
  ctx: SkuContext,
  currency: QuotationCurrency,
  price: QuotationUnitPrice,
  sub: boolean,
): void {
  if (price.kind === 'surcharge') {
    const label = surchargeLabel(price);
    pushPrice(lines, ctx, currency, `附加费：${label}`, perUnit(currency, price.unit_label.zh), price.usd_per_unit, true, '');
    return;
  }
  const spec = price.kind === 'flat' ? '' : (price.variant_label ?? '');
  pushPrice(
    lines,
    ctx,
    currency,
    spec,
    perUnit(currency, price.unit_label.zh),
    price.usd_per_unit,
    sub || price.kind === 'tier',
    '',
  );
}

function pushTokenImage(lines: PriceLine[], ctx: SkuContext, currency: QuotationCurrency, sku: QuotationSku): void {
  let based = false;
  if (sku.token_prices !== null) {
    for (const dim of IMAGE_DIMS) {
      const usd = dimUsd(sku.token_prices, dim.key);
      if (usd === null) continue;
      pushPrice(lines, ctx, currency, dim.label, tokenUnit(currency), usd, based, '');
      based = true;
    }
  }
  if (sku.token_tier_rows !== null) {
    for (const row of sku.token_tier_rows) {
      if (row.rates !== null && hasImageDim(row.rates)) {
        for (const dim of IMAGE_DIMS) {
          const usd = dimUsd(row.rates, dim.key);
          if (usd === null) continue;
          const spec = row.variant_label.length > 0 ? `${row.variant_label}｜${dim.label}` : dim.label;
          pushPrice(lines, ctx, currency, spec, tokenUnit(currency), usd, true, '');
        }
        continue;
      }
      if (row.single_rate_per_mtok !== null) {
        pushPrice(lines, ctx, currency, row.variant_label, sameRateUnit(currency), row.single_rate_per_mtok, true, '');
      }
    }
  }
  if (sku.unit_prices === null) return;
  for (const price of sku.unit_prices) {
    if (price.kind !== 'surcharge') continue;
    pushUnitPrice(lines, ctx, currency, price, true);
  }
}

function imageLines(
  included: readonly IncludedNamespace[],
  currency: QuotationCurrency,
): PriceLine[] {
  const lines: PriceLine[] = [];
  for (const entry of included) {
    for (const sku of entry.namespace.skus) {
      if (sku.category !== 'image') continue;
      const ctx = skuContext(entry, sku);
      if (sku.billing_mode === 'unit') {
        if (sku.unit_prices === null) continue;
        for (const price of sku.unit_prices) pushUnitPrice(lines, ctx, currency, price, false);
        continue;
      }
      pushTokenImage(lines, ctx, currency, sku);
    }
  }
  return lines;
}

function priceCells(line: PriceLine, fx: number): Cell[] {
  return [
    line.vendor,
    line.channel,
    line.model,
    line.spec,
    line.unit,
    listPrice(line.usd, fx),
    netPrice(line.usd, fx, line.zhe),
    line.discount,
    line.note,
  ];
}

function priceSummary(category: string, line: PriceLine, fx: number): SummaryLine {
  return {
    category,
    vendor: line.vendor,
    channel: line.channel,
    model: line.model,
    spec: line.spec,
    discount: line.discount,
    unit: line.unit,
    list: listPrice(line.usd, fx),
    net: netPrice(line.usd, fx, line.zhe),
    note: line.note,
  };
}

function buildImage(
  included: readonly IncludedNamespace[],
  currency: QuotationCurrency,
  fx: number,
): BuiltCategory {
  const lines = imageLines(included, currency);
  if (lines.length === 0) return { sheet: null, summary: [] };
  const header = ['厂商', '渠道', '模型名称', '规格', '计价单位', '单价(原价)', '单价(折后)', '折扣率', '备注'];
  return {
    sheet: finishSheet(
      IMAGE_NAME,
      header,
      lines.map((line) => priceCells(line, fx)),
      lines,
      1,
      2,
    ),
    summary: lines.map((line) => priceSummary('图片', line, fx)),
  };
}

function resolutionOf(variant: Readonly<Record<string, string>> | null): string | null {
  if (variant === null) return null;
  const value = variant.resolution;
  if (value === undefined || value.length === 0) return null;
  return value;
}

function columnLabel(raw: string | null): string {
  if (raw === null) return DEFAULT_COLUMN;
  return raw.toUpperCase();
}

function nonResKey(variant: Readonly<Record<string, string>> | null): string {
  if (variant === null) return '';
  const keys = Object.keys(variant)
    .filter((key) => key !== 'resolution')
    .sort();
  return keys
    .map((key) => {
      const value = variant[key];
      return `${key}=${value ?? ''}`;
    })
    .join('&');
}

/** Drop the resolution axis, then keep only the value after the first '='. */
function attrWithoutResolution(label: string | null): string {
  if (label === null || label.length === 0) return '';
  const kept: string[] = [];
  for (const part of label.split(', ')) {
    if (part.length === 0 || part.startsWith('分辨率=')) continue;
    const eq = part.indexOf('=');
    kept.push(eq < 0 ? part : part.slice(eq + 1));
  }
  return kept.join(', ');
}

/** Video surcharge 属性: value-only axis text, else `每` + unit. */
function videoSurchargeAttr(price: QuotationUnitPrice): string {
  const raw = price.variant_label;
  if (raw !== null && raw.length > 0) {
    const value = attrWithoutResolution(raw);
    if (value.length > 0) return `附加费：${value}`;
  }
  return `附加费：每${price.unit_label.zh}`;
}

function resolutionRank(value: string): number | null {
  const match = /^(\d+(?:\.\d+)?)([a-zA-Z]+)?$/.exec(value.trim());
  if (match === null) return null;
  const head = match[1];
  if (head === undefined) return null;
  const num = Number(head);
  if (!Number.isFinite(num)) return null;
  const suffix = (match[2] ?? '').toLowerCase();
  return suffix === 'k' ? num * 1000 : num;
}

function compareResolution(a: string, b: string): number {
  const left = resolutionRank(a);
  const right = resolutionRank(b);
  if (left !== null && right !== null && left !== right) return left - right;
  if (left !== null && right === null) return -1;
  if (left === null && right !== null) return 1;
  const leftLabel = a.toLowerCase();
  const rightLabel = b.toLowerCase();
  if (leftLabel < rightLabel) return -1;
  if (leftLabel > rightLabel) return 1;
  return 0;
}

function emptyBucket(surcharge: boolean, attr: string, unit: string): VideoBucket {
  return { surcharge, attr, unit, cells: new Map(), inputs: [] };
}

function writeCell(bucket: VideoBucket, raw: string | null, usd: number): void {
  const label = columnLabel(raw);
  if (bucket.cells.has(label)) return;
  bucket.cells.set(label, { raw, usd });
}

function rememberInput(bucket: VideoBucket, input: number | null): void {
  if (input === null || input === 0) return;
  if (!bucket.inputs.includes(input)) bucket.inputs.push(input);
}

function upsertBucket(
  buckets: VideoBucket[],
  index: Map<string, number>,
  key: string,
  attr: string,
  unit: string,
): VideoBucket {
  const existing = index.get(key);
  if (existing !== undefined) {
    const found = buckets[existing];
    if (found !== undefined) return found;
  }
  const created = emptyBucket(false, attr, unit);
  index.set(key, buckets.length);
  buckets.push(created);
  return created;
}

function videoBuckets(sku: QuotationSku, currency: QuotationCurrency): VideoBucket[] {
  const primary: VideoBucket[] = [];
  const primaryIndex = new Map<string, number>();
  const surcharges: VideoBucket[] = [];
  if (sku.unit_prices !== null) {
    for (const price of sku.unit_prices) {
      const unit = perUnit(currency, price.unit_label.zh);
      if (price.kind === 'surcharge') {
        const bucket = emptyBucket(true, videoSurchargeAttr(price), unit);
        writeCell(bucket, null, price.usd_per_unit);
        surcharges.push(bucket);
        continue;
      }
      const attr = price.kind === 'flat' ? '' : attrWithoutResolution(price.variant_label);
      const bucket = upsertBucket(
        primary,
        primaryIndex,
        `unit|${attr}|${unit}|${nonResKey(price.variant)}`,
        attr,
        unit,
      );
      writeCell(bucket, price.kind === 'flat' ? null : resolutionOf(price.variant), price.usd_per_unit);
    }
  }
  if (sku.token_tier_rows !== null) {
    for (const row of sku.token_tier_rows) {
      const usd = row.single_rate_per_mtok ?? (row.rates === null ? null : row.rates.output);
      if (usd === null) continue;
      const bucket = upsertBucket(
        primary,
        primaryIndex,
        `token|${attrWithoutResolution(row.variant_label)}|${nonResKey(row.variant)}`,
        attrWithoutResolution(row.variant_label),
        tokenUnit(currency),
      );
      writeCell(bucket, resolutionOf(row.variant), usd);
      rememberInput(bucket, row.rates === null ? null : row.rates.input);
    }
  }
  if (sku.token_tier_rows === null && sku.token_prices !== null && sku.token_prices.output !== null) {
    const bucket = emptyBucket(false, '', tokenUnit(currency));
    writeCell(bucket, null, sku.token_prices.output);
    rememberInput(bucket, sku.token_prices.input);
    primary.push(bucket);
  }
  return [...primary, ...surcharges];
}

function videoColumns(buckets: readonly VideoBucket[]): readonly string[] {
  const raws: string[] = [];
  let hasDefault = false;
  for (const bucket of buckets) {
    for (const cell of bucket.cells.values()) {
      if (cell.raw === null) {
        hasDefault = true;
        continue;
      }
      if (!raws.includes(cell.raw)) raws.push(cell.raw);
    }
  }
  raws.sort(compareResolution);
  const labels: string[] = [];
  for (const raw of raws) {
    const label = columnLabel(raw);
    if (!labels.includes(label)) labels.push(label);
  }
  return hasDefault ? [DEFAULT_COLUMN, ...labels] : labels;
}

function videoNote(inputs: readonly number[], fx: number): string {
  const parts: string[] = [];
  for (const usd of inputs) {
    const listed = listPrice(usd, fx);
    if (listed === null) continue;
    parts.push(`输入 ${String(listed)}`);
  }
  return parts.join('｜');
}

function videoSummarySpec(attr: string, column: string, surcharge: boolean): string {
  if (surcharge || column === DEFAULT_COLUMN) return attr;
  if (attr.length === 0) return column;
  return `${attr}｜${column}`;
}

interface VideoRow {
  readonly vendor: string;
  readonly channel: string;
  readonly model: string;
  readonly attr: string;
  readonly unit: string;
  readonly discount: string;
  readonly zhe: number | null;
  readonly surcharge: boolean;
  readonly cells: ReadonlyMap<string, VideoCell>;
  readonly note: string;
}

function buildVideo(
  included: readonly IncludedNamespace[],
  currency: QuotationCurrency,
  fx: number,
): BuiltCategory {
  const grouped: { readonly ctx: SkuContext; readonly buckets: readonly VideoBucket[] }[] = [];
  for (const entry of included) {
    for (const sku of entry.namespace.skus) {
      if (sku.category !== 'video') continue;
      grouped.push({ ctx: skuContext(entry, sku), buckets: videoBuckets(sku, currency) });
    }
  }
  const columns = videoColumns(grouped.flatMap((group) => [...group.buckets]));
  const rows: VideoRow[] = [];
  for (const group of grouped) {
    let headed = false;
    for (const bucket of group.buckets) {
      const sub = bucket.surcharge || headed;
      if (!bucket.surcharge) headed = true;
      rows.push({
        vendor: group.ctx.vendor,
        channel: group.ctx.channel,
        model: modelLabel(group.ctx.sku, sub),
        attr: bucket.attr,
        unit: bucket.unit,
        discount: group.ctx.discount,
        zhe: group.ctx.zhe,
        surcharge: bucket.surcharge,
        cells: bucket.cells,
        note: videoNote(bucket.inputs, fx),
      });
    }
  }
  if (rows.length === 0) return { sheet: null, summary: [] };
  const listHeaders = columns.map((column) => `${column}(原价)`);
  const netHeaders = columns.map((column) => `${column}(折后)`);
  const header = ['厂商', '渠道', '模型名称', '属性', '计价单位', ...listHeaders, ...netHeaders, '折扣率', '备注'];
  const sheetRows = rows.map((row) => {
    const cells: Cell[] = [row.vendor, row.channel, row.model, row.attr, row.unit];
    for (const column of columns) {
      const cell = row.cells.get(column);
      cells.push(listPrice(cell === undefined ? null : cell.usd, fx));
    }
    for (const column of columns) {
      const cell = row.cells.get(column);
      cells.push(netPrice(cell === undefined ? null : cell.usd, fx, row.zhe));
    }
    cells.push(row.discount, row.note);
    return cells;
  });
  const summary: SummaryLine[] = [];
  for (const row of rows) {
    for (const column of columns) {
      const cell = row.cells.get(column);
      if (cell === undefined) continue;
      summary.push({
        category: '视频',
        vendor: row.vendor,
        channel: row.channel,
        model: row.model,
        spec: videoSummarySpec(row.attr, column, row.surcharge),
        discount: row.discount,
        unit: row.unit,
        list: listPrice(cell.usd, fx),
        net: netPrice(cell.usd, fx, row.zhe),
        note: row.note,
      });
    }
  }
  return {
    sheet: finishSheet(VIDEO_NAME, header, sheetRows, rows, 1, 2),
    summary,
  };
}

function summarySheet(lines: readonly SummaryLine[]): Sheet {
  const header = [
    '类别',
    '厂商',
    '渠道',
    '模型名称',
    '规格/属性',
    '折扣率',
    '计价单位',
    '单价(原价)',
    '单价(折后)',
    '备注',
  ];
  const rows = lines.map((line) => [
    line.category,
    line.vendor,
    line.channel,
    line.model,
    line.spec,
    line.discount,
    line.unit,
    line.list,
    line.net,
    line.note,
  ]);
  return finishSheet(
    SUMMARY_NAME,
    header,
    rows,
    lines.map((line) => ({ vendor: line.vendor, channel: line.channel, category: line.category })),
    2,
    3,
  );
}

function includedSkuList(included: readonly IncludedNamespace[]): QuotationSku[] {
  const skus: QuotationSku[] = [];
  for (const entry of included) {
    for (const sku of entry.namespace.skus) skus.push(sku);
  }
  return skus;
}

function modelCount(skus: readonly QuotationSku[]): number {
  const keys = new Set<string>();
  for (const sku of skus) keys.add(`${sku.vendor}/${sku.model}`);
  return keys.size;
}

function currencyLabel(currency: QuotationCurrency): string {
  return currency === 'CNY' ? '人民币（CNY）' : '美元（USD）';
}

function fxLabel(currency: QuotationCurrency, fxRate: number): string {
  if (currency === 'CNY') return `1 USD = ${fxRate} CNY（导出当日展示汇率）`;
  return '价格以美元计，未换算';
}

function termsSheet(
  catalog: QuotationCatalogResponse,
  draft: QuotationDraft,
  included: readonly IncludedNamespace[],
): Sheet {
  const skus = includedSkuList(included);
  const customer = draft.customer_name.trim();
  const validDays = draft.valid_days.trim();
  const rows: readonly (readonly Cell[])[] = [
    ['客户', customer.length > 0 ? customer : '未命名'],
    ['报价日期', draft.quote_date],
    ['有效期', validDays.length > 0 ? `${validDays} 天` : ''],
    ['备注', draft.note],
    ['品牌', catalog.brand],
    ['币种', currencyLabel(draft.currency)],
    ['汇率', fxLabel(draft.currency, catalog.fx_rate)],
    ['拉取时间', formatShanghai(catalog.generated_at)],
    ['模型数', modelCount(skus)],
    ['命名空间数', included.length],
    ['原价口径', '原价 = 平台目录挂牌价，即租户在 /pricing 页看到的价格'],
    ['折后价口径', '折后价 = 原价 × 折扣；未填折扣的行折扣列为空，按原价执行'],
    ['空值含义', '空白 = 该模型没有这一计费维度，不是 0'],
    ['阶梯说明', '长上下文 / 分辨率阶梯行以「↳」标出；超过阈值后整卡按阶梯价执行'],
  ];
  const header = ['项', '说明'];
  return {
    name: TERMS_NAME,
    header,
    rows,
    merges: [],
    widths: header.map(widthFor),
  };
}

/** `报价单_<customer>_<date>.xlsx`. Blank customer becomes `未命名`. */
export function quotationFilename(draft: QuotationDraft): string {
  const trimmed = draft.customer_name.trim();
  const customer =
    trimmed.length === 0 ? '未命名' : trimmed.replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
  return `报价单_${customer}_${draft.quote_date}.xlsx`;
}

export function buildQuotationSheets(
  catalog: QuotationCatalogResponse,
  draft: QuotationDraft,
): QuotationSheets {
  const included = includedNamespaces(catalog, draft);
  const fx = draft.currency === 'CNY' ? catalog.fx_rate : 1;
  const text = buildText(included, draft.currency, fx);
  const image = buildImage(included, draft.currency, fx);
  const video = buildVideo(included, draft.currency, fx);
  return {
    summary: summarySheet([...text.summary, ...image.summary, ...video.summary]),
    text: text.sheet,
    image: image.sheet,
    video: video.sheet,
    terms: termsSheet(catalog, draft, included),
  };
}

function workbookSheets(sheets: QuotationSheets): Sheet[] {
  const present: Sheet[] = [];
  const ordered: readonly (Sheet | null)[] = [
    sheets.summary,
    sheets.text,
    sheets.image,
    sheets.video,
    sheets.terms,
  ];
  for (const sheet of ordered) {
    if (sheet !== null) present.push(sheet);
  }
  return present;
}

export async function downloadQuotationXlsx(sheets: QuotationSheets, filename: string): Promise<void> {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  for (const sheet of workbookSheets(sheets)) {
    const worksheet = workbook.addWorksheet(sheet.name);
    worksheet.addRow([...sheet.header]);
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    sheet.header.forEach((_title, index) => {
      const cell = headerRow.getCell(index + 1);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: FILL_YELLOW },
      };
    });
    for (const row of sheet.rows) {
      const added = worksheet.addRow(row.map((cell) => (cell === '' ? null : cell)));
      added.eachCell({ includeEmpty: false }, (cell) => {
        if (typeof cell.value === 'number') cell.numFmt = numberFormatFor(cell.value);
      });
    }
    for (const merge of sheet.merges) {
      worksheet.mergeCells(merge.fromRow, merge.column, merge.toRow, merge.column);
      const cell = worksheet.getCell(merge.fromRow, merge.column);
      cell.alignment = { vertical: 'middle' };
    }
    sheet.widths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
    worksheet.views = [{ state: 'frozen', ySplit: 1 }];
    if (sheet.name !== TERMS_NAME && sheet.header.length > 0) {
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: sheet.header.length },
      };
    }
  }
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
